import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { getAnnotationColor, getAnnotationIcon } from '../utils/annotationPresentation';
import {
  getAnnotationDescriptionDisplay,
  getAnnotationSeverityLabel,
  getAnnotationTypeLabel,
  getAnnotationUserLabel,
} from '../utils/annotationDescriptionDisplay';
import TaskAnnotationTooltip from './TaskAnnotationTooltip';
import NoteAnnotationTooltip from './NoteAnnotationTooltip';
import RepairAnnotationTooltip from './RepairAnnotationTooltip';
import AlarmAnnotationTooltip from './AlarmAnnotationTooltip';
import {
  TooltipContainer,
  TooltipHeader,
  HeaderLeft,
  IconCircle,
  HeaderTitle,
  TooltipDescription,
  TitleText,
  TooltipMeta,
  MetaItem,
  MetaIcon,
  SeverityPill
} from './TooltipCommon';
import { ClockIcon, UserIcon } from './MetaIcons';

// Layout helpers provided by TooltipCommon via styled-components

function formatDuration(startTime, endTime) {
  // Display a specific timestamp (and range when available) instead of relative/"Point in time"
  const start = moment(startTime);
  const end = endTime ? moment(endTime) : null;
  const timeFormat = 'DD/MM/YYYY HH:mm';

  if (!endTime || endTime === startTime) {
    return start.isValid() ? start.format(timeFormat) : '-';
  }

  const startStr = start.isValid() ? start.format(timeFormat) : '-';
  const endStr = end && end.isValid() ? end.format(timeFormat) : '-';
  return `${startStr} - ${endStr}`;
}

function AnnotationTooltip(props) {
  const { t } = useTranslation();
  const {
    annotation,
    position,
    showControls = true,
    onMouseEnter,
    onMouseLeave,
    onEdit
  } = props;

  if (annotation && annotation.type === 'task') {
    return (
      <TaskAnnotationTooltip
        annotation={annotation}
        position={position}
        showControls={showControls}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onEdit={onEdit}
      />
    );
  }

  if (annotation && annotation.type === 'note') {
    return (
      <NoteAnnotationTooltip
        annotation={annotation}
        position={position}
        showControls={showControls}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    );
  }

  if (annotation && annotation.type === 'repair') {
    return (
      <RepairAnnotationTooltip
        annotation={annotation}
        position={position}
        showControls={showControls}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onEdit={onEdit}
      />
    );
  }

  if (annotation && annotation.type === 'alarm') {
    return (
      <AlarmAnnotationTooltip
        annotation={annotation}
        position={position}
        showControls={showControls}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    );
  }

  const typeColor = getAnnotationColor(annotation, { respectSeverity: false });
  const typeIcon = getAnnotationIcon(annotation.type);
  const description = getAnnotationDescriptionDisplay(t, annotation);

  const tooltip = (
    <TooltipContainer
      style={{
        left: position.x,
        top: position.y
      }}
      placement={position.placement || 'top'}
      align={position.align || 'center'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <TooltipHeader>
        <HeaderLeft>
          <IconCircle bg={typeColor}>
            <span aria-hidden="true">{typeIcon}</span>
          </IconCircle>
          <HeaderTitle>
            {getAnnotationTypeLabel(t, annotation.type)}
            {annotation.severity && (
              <SeverityPill severity={annotation.severity}>
                {getAnnotationSeverityLabel(t, annotation.severity)}
              </SeverityPill>
            )}
          </HeaderTitle>
        </HeaderLeft>
      </TooltipHeader>

      {annotation.title && (
        <TitleText>
          {annotation.title}
        </TitleText>
      )}

      {description && (
        <TooltipDescription>
          {description}
        </TooltipDescription>
      )}

      <TooltipMeta>
        <div className="meta-row">
          <MetaItem>
            <MetaIcon><ClockIcon size={14} /></MetaIcon>
            <span>{formatDuration(annotation.start_time, annotation.end_time)}</span>
          </MetaItem>
          <MetaItem>
            <MetaIcon><UserIcon size={14} /></MetaIcon>
            <span>{getAnnotationUserLabel(t, annotation)}</span>
          </MetaItem>
        </div>
      </TooltipMeta>

      {/* Bottom actions removed: header contains View Details */}
    </TooltipContainer>
  );

  return createPortal(tooltip, document.body);
}

AnnotationTooltip.propTypes = {
  annotation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    start_time: PropTypes.string.isRequired,
    end_time: PropTypes.string,
    description: PropTypes.string,
    severity: PropTypes.oneOf(['low', 'medium', 'high', 'critical']),
    created_by: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    created_at: PropTypes.string,
    source_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    base_metadata: PropTypes.object,
    user_metadata: PropTypes.object
  }).isRequired,

  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    placement: PropTypes.string,
    align: PropTypes.string
  }).isRequired,

  showControls: PropTypes.bool,

  onEdit: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func
};

AnnotationTooltip.defaultProps = {
  showControls: true,
  onEdit: null,
  onMouseEnter: null,
  onMouseLeave: null
};

export default AnnotationTooltip;
